/**
 * A percentage somebody has to read off a Slovak screen.
 *
 * `toFixed` writes a **dot**, so a share renders as `54.1 %` on a page whose every other number uses
 * a comma. It is the same trap as FB-105's CSV export, one screen earlier: the locale is not optional
 * just because the value is small.
 *
 * `decimals` is a **maximum**, not a fixed width, so `100` stays `100 %` rather than becoming
 * `100,00 %` — a whole number with two zeros after it reads as a measurement to two decimal places.
 *
 * Third caller and counting (`/statistics`, `/analytics` twice), which is why it is here and not in
 * either module.
 */
const FORMATS = new Map<number, Intl.NumberFormat>()

function formatterFor(decimals: number): Intl.NumberFormat {
  const existing = FORMATS.get(decimals)
  if (existing !== undefined) return existing
  const created = new Intl.NumberFormat('sk-SK', { maximumFractionDigits: decimals })
  FORMATS.set(decimals, created)
  return created
}

/**
 * A decimal number the Slovak way — `54.14` → `54,14`, `31.6` at 0 decimals → `32`.
 *
 * Not only for percentages: an average of 2,94 orders per customer and a p95 of 64 minutes have the
 * same problem and the same fix.
 */
export function formatDecimal(value: number, decimals = 2): string {
  return formatterFor(decimals).format(Number.isFinite(value) ? value : 0)
}

/**
 * A decimal at a **fixed** width — `1.419` at four decimals → `1,4190`.
 *
 * `formatDecimal`'s width is a maximum on purpose, so `100` stays `100` rather than `100,00`. That is right
 * for a percentage and wrong for a quoted price: a supplier who quotes 1,4190 € per unit has quoted four
 * decimals, and dropping the trailing zero drops the precision they stated.
 *
 * Reached in §9.3's sweep, where `/supplier-comparison` had built it by hand out of
 * `toFixed(4).replace('.', ',')` — which is right about the comma and silent about the thousands separator.
 */
export function formatFixed(value: number, decimals: number): string {
  return new Intl.NumberFormat('sk-SK', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(Number.isFinite(value) ? value : 0)
}

/** `54.14` → `54,14 %`. */
export function formatPercent(value: number, decimals = 2): string {
  return `${formatDecimal(value, decimals)} %`
}

// `formatPercentChange` was here and is deleted — §9.35, 2026-08-06. A spare part rather than a trap:
// correct, and for a feature that does not exist. Nothing in either app renders a percentage
// *change* — there is no compare-to-previous-period anywhere in the dashboard or the reports — so it
// had no caller and no candidate for one.
//
// The rule inside it is the part worth not losing, and it belongs to whoever builds that comparison:
// `null` is *unknown* and must not render as 0 %, because a change against no baseline is not a
// change of nought. Written here rather than kept as code, since an implementation nobody can call
// is a claim nobody can check.

/**
 * A byte count for a human - `SAAS.md` §3.42.
 *
 * **Binary units, and that is the decision rather than a detail.** A database quoted in decimal MB
 * against a disk quoted in binary MiB is how two correct numbers disagree by 5 % and somebody
 * concludes the figure is broken. `dbSizeBytes` comes from `pg_database_size`, which counts bytes, so
 * 1 024 is the divisor.
 *
 * **Nought renders as `0 B`, not blank.** A franchise whose database is empty and one whose size
 * could not be read are different states, and the second is already reported as an error on the row.
 *
 * The number itself goes through `formatDecimal`, so the separator is the Slovak one like every other
 * figure on the screen.
 */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B'
  const units = ['B', 'kiB', 'MiB', 'GiB', 'TiB']
  let value = bytes
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit += 1
  }
  // Whole bytes have no fractional part worth printing; everything above shows one place.
  //
  // **`formatFixed` above the boundary, not `formatDecimal`** - the second time that distinction has
  // come up in this review (§3.37 was the first). `formatDecimal`'s width is a **maximum**, so
  // exactly 1 MiB renders as `1 MiB` where the column beside it reads `1,5 MiB`; a fixed width is
  // what a column of figures wants. Below 1 024 there is no fraction to fix, and `formatDecimal`
  // still groups the thousands - `1 023 B`, which is the Slovak separator every other figure uses.
  return unit === 0
    ? `${formatDecimal(value, 0)} ${units[unit]}`
    : `${formatFixed(value, 1)} ${units[unit]}`
}
